import { Injectable } from '@angular/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { AccessPaths, AppPaths, OrderPaths } from '@ftr/routing-paths'
import { UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { map, Observable, of, switchMap } from 'rxjs'
import { InternalUserNavItemsProvider } from './internal/internal-user-nav-items.provider'
import { SideNavGroup, SideNavItem } from './side-nav-item'

@Injectable({
  providedIn: 'root',
})
export class PublicSideNavItemsProvider {
  constructor(
    private readonly store: Store,
    private readonly internalUserNavItemsProvider: InternalUserNavItemsProvider,
  ) {}

  provide(courtSystemId?: Uuid): Observable<{ navGroups: SideNavGroup[]; footerItems: SideNavItem[] }> {
    if (!courtSystemId) {
      return this.providePublicNav()
    }

    const hasPermissionsInCourtSystem = this.store
      .select(UserState.getEnabledPermissions)
      .pipe(map(fn => fn(courtSystemId).length > 0))

    return hasPermissionsInCourtSystem.pipe(
      switchMap(hasPermissions => {
        if (hasPermissions) {
          return this.internalUserNavItemsProvider.provide(courtSystemId)
        }
        return this.providePublicNav()
      }),
    )
  }

  private providePublicNav(): Observable<{ navGroups: SideNavGroup[]; footerItems: SideNavItem[] }> {
    const navGroups: SideNavGroup[] = []
    const footerItems: SideNavItem[] = []

    navGroups.push({
      title: 'Request',
      items: [
        {
          type: 'LINK',
          icon: 'Recordings',
          title: 'Recordings',
          path: [AppPaths.Orders, OrderPaths.ChooseCourt, OrderPaths.Audio],
        } as SideNavItem,
        {
          type: 'LINK',
          icon: 'Transcript',
          title: 'Transcripts',
          path: [AppPaths.Orders, OrderPaths.ChooseCourt, OrderPaths.Transcript],
        } as SideNavItem,
      ],
    })

    navGroups.push({
      title: 'Access',
      items: [
        {
          type: 'LINK',
          icon: 'Folder',
          title: 'Content',
          path: [AppPaths.Access, AccessPaths.Content],
        } as SideNavItem,
        {
          type: 'LINK',
          icon: 'CardChart', // TODO: replace this with figma icon
          title: 'Requests',
          path: [AppPaths.Orders],
        } as SideNavItem,
        {
          type: 'LINK',
          icon: 'TranscriptJobs',
          title: 'Transcript Jobs',
          path: [AppPaths.Jobs],
        } as SideNavItem,
      ],
    })

    footerItems.push({
      type: 'LINK',
      icon: 'Gavel',
      title: 'Enter a Court System',
      path: [AppPaths.CourtSystem],
    })
    return of({ navGroups, footerItems })
  }
}
